import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/cn';

const cardVariants = cva(
  'rounded-lg border bg-surface text-ink shadow-soft transition-[box-shadow,transform,border-color]',
  {
    variants: {
      variant: {
        default: 'border-border',
        interactive: 'border-border hover:border-ink/15 hover:bg-surface-muted/40 cursor-pointer',
        elevated: 'border-border shadow-elevated',
        flat: 'border-border shadow-none',
      },
      padding: {
        none: 'p-0',
        sm: 'p-3',
        md: 'p-5',
        lg: 'p-6',
      },
    },
    defaultVariants: { variant: 'default', padding: 'md' },
  },
);

type CardVariants = VariantProps<typeof cardVariants>;

interface CardProps extends HTMLAttributes<HTMLDivElement>, CardVariants {
  children: ReactNode;
}

export function Card({ variant, padding, className, children, ...rest }: CardProps) {
  return (
    <div className={cn(cardVariants({ variant, padding }), className)} {...rest}>
      {children}
    </div>
  );
}

export function CardHeader({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('mb-4 flex flex-col gap-1', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardContent({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex flex-col gap-3', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardTitle({ className, children, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn('text-lg font-semibold text-ink', className)} {...rest}>
      {children}
    </h3>
  );
}

export function CardDescription({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn('text-sm text-muted', className)} {...rest}>
      {children}
    </p>
  );
}

export function CardFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('mt-6 flex items-center gap-3', className)} {...rest}>
      {children}
    </div>
  );
}
